// client/src/coms/views/chatview_parts/StickerSuggestions.js
// LINE-style sticker suggestions above the composer: typing a word that matches sticker keywords shows the
// matching stickers from every pack the user may use in this channel. Matching is local (no request per
// keystroke). Keyboard: ↑ or Tab enters the strip, ←/→ move, Enter sends, ↓ returns to the text, Esc dismisses.
import React, { useEffect, useMemo, useState } from 'react';
import { stickerResolver } from '../../customMedia';
import { loadSuggestionPacks, STICKERS_CHANGED_EVENT, suggestStickers, usableStickers, wordAtCaret } from '../../stickers/stickerSuggest';

export function useStickerSuggestions({ enabled, draft, caret, serverKey, canSendServer, canSendExternal, blocked }) {
  const [packs, setPacks] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [dismissed, setDismissed] = useState('');

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setPacks([]); // never suggest another conversation's stickers while this one loads
    const load = (force) =>
      loadSuggestionPacks({ serverKey, force }).then((list) => {
        if (!alive) return;
        setPacks(list);
        stickerResolver.prime(list.filter((p) => !p.local).flatMap((p) => p.stickers || []));
      });
    load(false);
    const onChanged = () => load(true);
    window.addEventListener(STICKERS_CHANGED_EVENT, onChanged);
    return () => {
      alive = false;
      window.removeEventListener(STICKERS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, serverKey]);

  const index = useMemo(() => usableStickers(packs, { serverKey, canSendServer, canSendExternal }), [packs, serverKey, canSendServer, canSendExternal]);
  const target = useMemo(() => (enabled && !blocked && index.length ? wordAtCaret(draft, caret) : null), [enabled, blocked, index, draft, caret]);
  const targetKey = target ? `${target.start}:${target.word}` : '';
  const items = useMemo(() => (target ? suggestStickers(index, target.word) : []), [index, target]);
  const open = items.length > 0 && targetKey !== dismissed;

  // A new word starts outside the strip; a dismissed word comes back once the user types something else.
  useEffect(() => {
    setHighlight(-1);
    setDismissed((d) => (d && d !== targetKey ? '' : d));
  }, [targetKey]);

  const dismiss = () => {
    setDismissed(targetKey);
    setHighlight(-1);
  };

  // Returns true when the key was handled. `pick(sticker)` sends it.
  function handleKeyDown(e, pick) {
    if (!open) return false;
    const key = String(e.key || '');
    const stop = () => {
      e.preventDefault();
      e.stopPropagation();
    };
    if (key === 'Escape') {
      stop();
      if (highlight >= 0) setHighlight(-1);
      else dismiss();
      return true;
    }
    if (highlight < 0) {
      if ((key === 'ArrowUp' || key === 'Tab') && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
        stop();
        setHighlight(0);
        return true;
      }
      return false;
    }
    if (key === 'ArrowRight' || (key === 'Tab' && !e.shiftKey)) {
      stop();
      setHighlight((h) => (h + 1) % items.length);
      return true;
    }
    if (key === 'ArrowLeft' || (key === 'Tab' && e.shiftKey)) {
      stop();
      setHighlight((h) => (h - 1 + items.length) % items.length);
      return true;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      stop();
      setHighlight(-1);
      return true;
    }
    if (key === 'Enter' && !e.shiftKey) {
      stop();
      pick(items[Math.min(highlight, items.length - 1)]);
      return true;
    }
    return false;
  }

  return { open, items, target, highlight, setHighlight, dismiss, handleKeyDown };
}

export const STICKER_SUGGESTION_LIST_ID = 'vox-sticker-suggestions';
export const suggestionOptionId = (s) => `vox-sticker-suggestion-${s.id}`;

export default function StickerSuggestionBar({ items, target, highlight, onPick, onHighlight, onDismiss, isShellMobile }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: isShellMobile ? 8 : 0,
        right: isShellMobile ? 8 : 0,
        bottom: 'calc(100% + 8px)',
        zIndex: 3002,
        borderRadius: 12,
        border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
        boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
        background: 'rgba(var(--vxs-1-rgb, 15,18,24), 0.98)',
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))', fontSize: 11, userSelect: 'none' }}>
        <span style={{ fontWeight: 800, opacity: 0.75 }}>
          Stickers for “{target ? target.raw : ''}”
        </span>
        <span style={{ opacity: 0.5 }}>{isShellMobile ? 'Tap to send' : '↑ to choose · Enter to send · Esc to close'}</span>
        <button
          type="button"
          aria-label="Close sticker suggestions"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onDismiss}
          style={{ marginLeft: 'auto', border: 0, background: 'transparent', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.6 * var(--vx-ov, 1)))', cursor: 'pointer', fontSize: 13 }}
        >
          ✕
        </button>
      </div>
      <div
        id={STICKER_SUGGESTION_LIST_ID}
        role="listbox"
        aria-label={`Sticker suggestions for ${target ? target.raw : ''}`}
        aria-orientation="horizontal"
        style={{ display: 'flex', gap: 6, padding: 8, overflowX: 'auto' }}
      >
        {items.map((s, i) => {
          const active = i === highlight;
          return (
            <button
              key={s.id}
              id={suggestionOptionId(s)}
              type="button"
              role="option"
              aria-selected={active}
              aria-label={`${s.name}${s.packName ? ` (${s.packName})` : ''}`}
              title={`${s.name} · ${s.packName}`}
              tabIndex={-1}
              // Keep focus (and the caret) in the composer.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => onHighlight(i)}
              // A suggestion is a send action, never an image-preview action.
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPick(s);
              }}
              style={{
                flex: '0 0 auto',
                width: 72,
                height: 72,
                padding: 4,
                borderRadius: 10,
                border: active ? '2px solid rgba(var(--vx-link-rgb, 110,160,255), 0.9)' : '2px solid transparent',
                background: active ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
                cursor: 'pointer',
              }}
            >
              <img src={s.url} alt="" draggable={false} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
